import 'chartjs-adapter-moment'

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core'
import { Chart, ChartDataset, ChartOptions } from 'chart.js/auto'
import moment from 'moment'
import { BaseChartDirective } from 'ng2-charts'

import { buildPointStyles, insertSplitDataPoint, TooltipLine } from '../utils'
import { ChartUtilities } from '../utils/chart-utlities'

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
})
export class LineChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective, { static: false }) chart: BaseChartDirective

  @Input() chartHeight: string
  @Input() dataset: ChartDataset[]
  @Input() labels: string[]
  @Input() ariaLabel: string
  @Input() xMaxTicksLimit: number
  @Input() xScaleTitle: string
  @Input() shareSplitDate: string | null = null
  @Input() shareSplitPrice: number | null = null
  yScaleTitle = 'Price'
  private splitIndex = -1

  @Output() chartMouseOut = new EventEmitter<any>()
  @Output() activeItemChanged = new EventEmitter<any>()
  @Output() dataUpdated = new EventEmitter<{
    labels: string[]
    datasets: any[]
    splitIndex: number
  }>()

  private lastActiveItem: any
  private ticksArray: any[]
  options: ChartOptions
  data: any

  ngOnChanges() {
    const { labels, datasets, splitIndex } = insertSplitDataPoint(
      this.labels,
      this.dataset ?? [],
      this.shareSplitDate,
      this.shareSplitPrice,
    )
    this.splitIndex = splitIndex

    const pointStyles = buildPointStyles(
      datasets?.[0]?.data?.length ?? 0,
      this.splitIndex,
    )

    if (datasets?.[0]) {
      datasets[0] = { ...datasets[0], ...pointStyles }
    }

    this.data = {
      labels,
      datasets,
    }

    this.dataUpdated.emit({ labels, datasets, splitIndex })

    this.options = {
      borderColor: '#1BA39C',
      datasets: {
        line: {
          hoverBackgroundColor: 'rgb(75, 192, 192)',
          hoverBorderJoinStyle: 'bevel',
          pointStyle: 'circle',
          pointHoverBackgroundColor: 'rgb(75, 192, 192)',
          pointHoverRadius: 5,
          pointRadius: 1,
          pointBorderColor: 'rgb(75, 192, 192)',
          pointBackgroundColor: 'rgb(75, 192, 192)',
        },
      },
      responsive: true,
      hover: {
        mode: 'index',
        intersect: false,
      },
      onHover: (event, activeList) => this.chartHovered(event, activeList),
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          intersect: false,
          mode: 'index',
          callbacks: {
            title: (items) => moment(items[0]?.label).format('MMM/DD/YYYY'),
            label: (item) => {
              const defaultLabel = `$${Number(item.formattedValue).toFixed(2)}`
              if (this.splitIndex >= 0 && item.dataIndex === this.splitIndex) {
                return 'Share split'
              }
              return defaultLabel
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          stacked: true,
          title: {
            display: false,
          },
          grid: {
            display: false,
          },
          beforeUpdate: () => (this.ticksArray = []),
          ticks: {
            maxTicksLimit: 6,
            maxRotation: 0,
            padding: 8,
            callback: (tick, index, ticks) =>
              ChartUtilities.pruneXAxisTicks(
                tick,
                index,
                ticks,
                this.xMaxTicksLimit,
                this.ticksArray,
              ),
          },
        },
        y: {
          beginAtZero: false,
          title: {
            display: Boolean(this.yScaleTitle),
            text: this.yScaleTitle,
          },
          grid: {
            display: true,
          },
          border: {
            display: false,
          },
          ticks: {
            maxTicksLimit: 4,
            padding: 20,
            callback: function (value) {
              return `$${value}`
            },
          },
          afterBuildTicks: (chart) => (chart.ticks = chart.ticks.slice(1)),
          afterFit: (scaleInstance) => (scaleInstance.width = 100),
        },
      },
    }
  }

  constructor() {
    Chart.register(TooltipLine)
  }

  get splitDateInRange(): boolean {
    if (!this.shareSplitDate || !this.labels || this.labels.length === 0)
      return false
    const splitTime = new Date(this.shareSplitDate).getTime()
    const firstDate = new Date(this.labels[0]).getTime()
    const lastDate = new Date(this.labels[this.labels.length - 1]).getTime()
    return splitTime >= firstDate && splitTime <= lastDate
  }

  chartHovered(event, activeList: any[]): void {
    if (event.type === 'mouseout') {
      this.chartMouseOut.emit(event)
    } else if (
      activeList &&
      activeList.length &&
      activeList[0] !== this.lastActiveItem
    ) {
      this.lastActiveItem = activeList[0]
      this.activeItemChanged.emit(this.lastActiveItem)
    }
  }
}
